import { QuestionPType, TagCategory, type GetQuestionByIdQuery } from '@exam-management/graphql';
import type {
  QBankStructureLevel,
  QBankStructureLevelItemWithLevel,
} from '@exam-management/qbs-provider';

import type { ItemSetSubQuestionEditFormInput } from './types';

function convertToPType(selected: QuestionPType | null): 'pInitial' | 'pStat' {
  switch (selected) {
    case QuestionPType.PInitial:
      return 'pInitial';
    case QuestionPType.PStat:
      return 'pStat';
    default:
      throw new Error('Edit form should have completed pType information');
  }
}

export async function getItemSetSubQuestionEditFormDefaultValues(
  questions: GetQuestionByIdQuery['qctGetQuestionById'] | undefined,
  qbsLevels: QBankStructureLevel[],
  qbsLevelItems: QBankStructureLevelItemWithLevel[],
  parseContentForDisplay: (content: string) => Promise<string>,
) {
  if (!questions || !questions.questionGroup) return;
  const {
    pType,
    pInitial,
    tags,
    metadata,
    questionStructures,
    details,
    correctAnswer,
    solution,
    learningObjectiveStatement,
  } = questions;
  const { choices, reference, audit1, audit2, audit3, owner, pStats } = metadata;
  const levelItemLocation: Record<string, { label: string; value: string } | undefined> = {};
  const locations = qbsLevelItems.filter((qBankStructureLevelItem) =>
    questionStructures?.some(
      (questionStructure) => questionStructure.refLevelItemId === qBankStructureLevelItem.id,
    ),
  );

  for (const level of qbsLevels) {
    const currentCategory = level.category;
    const levelItem = locations.find(
      (location) => location.qBankStructureLevel.category === currentCategory,
    );
    if (!levelItem) {
      levelItemLocation[currentCategory] = undefined;
      continue;
    }

    levelItemLocation[currentCategory] = {
      label: levelItem.tag?.name ?? 'ไม่ระบุ',
      value: levelItem.id,
    };
  }

  const parsedDetails = await parseContentForDisplay(details ?? '');
  const parsedCorrectAnswer = { value: correctAnswer?.value ?? undefined };
  const parsedSolution = await parseContentForDisplay(solution?.value ?? '');
  const parsedReference = await parseContentForDisplay(reference ?? '');

  const parsedChoices: ItemSetSubQuestionEditFormInput['detail']['choices'] = [];
  for (const choice of choices) {
    const label = await parseContentForDisplay(choice.label);
    parsedChoices.push({
      order: choice.order,
      value: choice.value,
      label,
    });
  }

  const los = learningObjectiveStatement
    ? { label: learningObjectiveStatement.tag.name, value: learningObjectiveStatement.id }
    : undefined;

  const calculation = tags.find((tag) => tag.category === TagCategory.Calculation);
  const cognitiveDomain: ItemSetSubQuestionEditFormInput['properties']['cognitiveDomain'] = [];

  for (const tag of tags) {
    if (tag.category === TagCategory.CognitiveDomain) {
      cognitiveDomain.push({ label: tag.name, value: tag.id });
    }
  }

  return {
    detail: {
      details: parsedDetails,
      choices:
        parsedChoices.length > 0
          ? parsedChoices
          : [
              { value: '1', label: '', order: 1 },
              { value: '2', label: '', order: 2 },
              { value: '3', label: '', order: 3 },
              { value: '4', label: '', order: 4 },
            ],
      correctAnswer: parsedCorrectAnswer,
    },
    properties: {
      solution: parsedSolution,
      cognitiveDomain,
      reference: parsedReference,
      learningObjectiveStatement: los,
    },
    information: {
      /*pValue: {
      selected: convertPTypeToSelectedValue(pType),
      pInitial: pInitial ?? undefined,
      pStat: pStats?.find((stat) => stat.selected)?.pValue,
    },
    calculated: calculation ? calculatedValue : '',
    bIndex: bIndexValue ?? '',
    pStats: pStats ?? null,
      */
      pValue: {
        //TODO: make selected to be 'pStat' if pStat is not null - in the future
        selected: convertToPType(pType),
        pInitial: typeof pInitial === 'number' && !Number.isNaN(pInitial) ? pInitial : undefined,
        pStat: pStats?.find((stat) => stat.selected)?.pValue,
      },
      pStats: pStats ?? null,
      bIndex: questions.bIndexValue ?? undefined,
      calculated: calculation ? (calculation.name === 'คำนวณ' ? 'true' : 'false') : '',
    },
    owners: {
      owner: owner?.value ?? '',
      audit1: audit1 ?? '',
      audit2: audit2 ?? '',
      audit3: audit3 ?? '',
    },
    location: {
      ...levelItemLocation,
    },
  };
}
